import React, { useState } from 'react';

// ── Rarity tier system ──────────────────────────────────────────────────────
const TIERS = [
  { id: 'platinum', label: 'Platinum', min: 95, color: '#00d4ff' },
  { id: 'gold',     label: 'Gold',     min: 80, color: '#f59e0b' },
  { id: 'silver',   label: 'Silver',   min: 65, color: '#94a3b8' },
  { id: 'bronze',   label: 'Bronze',   min: 50, color: '#cd7f32' },
];

function getTier(score) {
  return TIERS.find((t) => score >= t.min) || null;
}

// ── Dimension badges (one per dimension, upgrades by tier) ──────────────────
const DIMENSION_BADGES = [
  {
    id: 'raw_speed',
    label: 'Reaction King',
    dimension: 'rawSpeed',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
  },
  {
    id: 'decision',
    label: 'Cold Logic',
    dimension: 'decisionQuality',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
      </svg>
    ),
  },
  {
    id: 'aim',
    label: 'Sharpshooter',
    dimension: 'aimPrecision',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="3" />
        <line x1="12" y1="3" x2="12" y2="7" />
        <line x1="12" y1="17" x2="12" y2="21" />
        <line x1="3" y1="12" x2="7" y2="12" />
        <line x1="17" y1="12" x2="21" y2="12" />
      </svg>
    ),
  },
  {
    id: 'tracking',
    label: 'Ghost Tracker',
    dimension: 'trackingAccuracy',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M21 12a9 9 0 1 1-6.22-8.56" strokeDasharray="3 2" />
        <circle cx="18" cy="6" r="2" fill="currentColor" fillOpacity="0.2" />
        <circle cx="12" cy="12" r="2" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'memory',
    label: 'Mind Palace',
    dimension: 'workingMemory',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" opacity="0.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" opacity="0.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    id: 'consistency',
    label: 'Metronome',
    dimension: 'consistency',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M12 2v20M6 6l12 12M18 6L6 18" strokeWidth="1.2" opacity="0.4" />
        <path d="M5 12h14" />
        <circle cx="12" cy="12" r="2" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'flick',
    label: 'Flick God',
    dimension: 'flickSpeed',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="12" r="2.5" fill="currentColor" fillOpacity="0.2" />
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
        <path d="M6.4 6.4l2 2M15.6 15.6l2 2M6.4 17.6l2-2M15.6 8.4l2-2" strokeWidth="1.4" />
      </svg>
    ),
  },
  {
    id: 'prioritization',
    label: 'Tactician',
    dimension: 'prioritization',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="5" cy="5" r="2.5" />
        <circle cx="19" cy="5" r="2.5" />
        <circle cx="19" cy="19" r="2.5" />
        <circle cx="5" cy="19" r="2.5" />
        <path d="M7.5 5h9M19 7.5v9M16.5 19h-9M5 16.5v-9" strokeWidth="1.2" strokeDasharray="2 1.5" opacity="0.5" />
      </svg>
    ),
  },
];

// ── Score milestone badges (overall) ───────────────────────────────────────
const MILESTONE_BADGES = [
  {
    id: 'perfect_mind',
    label: 'Perfect Mind',
    desc: 'Overall score of 95 or above — top 5% of all players.',
    color: '#f59e0b',
    check: (s) => (s.overallScore ?? 0) >= 95,
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
    ),
  },
  {
    id: 'elite_performer',
    label: 'Elite Performer',
    desc: 'Overall score of 85 or above.',
    color: '#00d4ff',
    check: (s) => (s.overallScore ?? 0) >= 85,
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
      </svg>
    ),
  },
  {
    id: 'well_rounded',
    label: 'Well Rounded',
    desc: 'All scored dimensions at 60 or above.',
    color: '#10b981',
    check: (s) => {
      const dims = ['rawSpeed', 'decisionQuality', 'aimPrecision', 'trackingAccuracy', 'workingMemory', 'consistency', 'flickSpeed', 'prioritization'];
      const scored = dims.filter((d) => s[d] !== undefined);
      return scored.length >= 4 && scored.every((d) => (s[d] ?? 0) >= 60);
    },
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v18M3 12h18" strokeWidth="1.2" opacity="0.5" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
];

// ── Behavioural badges (require badgeContext) ───────────────────────────────
const BEHAVIOURAL_BADGES = [
  {
    id: 'first_blood',
    label: 'First Blood',
    desc: 'Completed your first combine.',
    color: '#ef4444',
    check: (ctx) => (ctx.combineCount || 0) >= 1,
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C12 2 5 10 5 15a7 7 0 0 0 14 0C19 10 12 2 12 2z" />
      </svg>
    ),
  },
  {
    id: 'road_warrior',
    label: 'Road Warrior',
    desc: 'Completed 3 or more combines.',
    color: '#f59e0b',
    check: (ctx) => (ctx.combineCount || 0) >= 3,
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 17l3-9 6 4 6-4 3 9" />
        <path d="M3 17h18" />
      </svg>
    ),
  },
  {
    id: 'tri_athlete',
    label: 'Tri-Athlete',
    desc: 'Completed a combine in all three genres: General, FPS, and MOBA.',
    color: '#a855f7',
    check: (ctx) => {
      const g = ctx.completedGenres || [];
      return g.includes('general') && g.includes('fps') && g.includes('moba');
    },
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12,3 22,21 2,21" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8.5" y1="16" x2="15.5" y2="16" />
      </svg>
    ),
  },
];

// ── Referral badges ─────────────────────────────────────────────────────────
const REFERRAL_BADGES = [
  {
    id: 'recruiter',
    label: 'Recruiter',
    desc: 'Referred 1 or more players to NeuroRank.',
    color: '#10b981',
    check: (ctx) => (ctx.referralCount || 0) >= 1,
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="7" r="4" />
        <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
        <path d="M19 8v6M22 11h-6" />
      </svg>
    ),
  },
  {
    id: 'talent_scout',
    label: 'Talent Scout',
    desc: 'Referred 5 or more players to NeuroRank.',
    color: '#f59e0b',
    check: (ctx) => (ctx.referralCount || 0) >= 5,
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="7" r="4" />
        <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
        <circle cx="19" cy="7" r="3" />
        <path d="M22 21v-1.5a3 3 0 0 0-3-3h-1" />
      </svg>
    ),
  },
];

// ── Badge components ────────────────────────────────────────────────────────
function Badge({ svg, label, color, desc, earned, tierLabel }) {
  const [showTip, setShowTip] = useState(false);

  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '6px 12px', borderRadius: 20,
        border: `1px solid ${earned ? color + '66' : 'var(--border)'}`,
        background: earned ? color + '18' : 'var(--bg-secondary)',
        opacity: earned ? 1 : 0.3,
        cursor: 'default',
        transition: 'opacity 0.15s',
      }}>
        <span style={{ color: earned ? color : 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
          {svg}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: earned ? color : 'var(--text-muted)' }}>
          {label}
        </span>
        {earned && tierLabel && (
          <span style={{ fontSize: 10, fontWeight: 600, color: color, opacity: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {tierLabel}
          </span>
        )}
      </div>

      {showTip && (
        <div style={{
          position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '8px 12px', whiteSpace: 'nowrap',
          zIndex: 10, pointerEvents: 'none',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          <p style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 700, margin: '0 0 2px' }}>
            {label}{tierLabel ? ` · ${tierLabel}` : ''}
          </p>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{desc}</p>
          {!earned && <p style={{ fontSize: 10, color: '#ef4444', margin: '4px 0 0', fontStyle: 'italic' }}>Not yet earned</p>}
        </div>
      )}
    </div>
  );
}

// ── Exports ─────────────────────────────────────────────────────────────────
export function getEarnedBadges(scores) {
  return MILESTONE_BADGES.filter((b) => b.check(scores));
}

export default function ProfileBadges({ scores, badgeContext }) {
  if (!scores) return null;

  // Build dimension badge list (only for dimensions present in this genre's scores)
  const dimBadges = DIMENSION_BADGES
    .filter((b) => scores[b.dimension] !== undefined)
    .map((b) => {
      const score = scores[b.dimension] ?? 0;
      const tier = getTier(score);
      return { ...b, tier, score };
    });

  const earnedDim = dimBadges.filter((b) => b.tier !== null);
  const unearnedDim = dimBadges.filter((b) => b.tier === null);

  // Milestone badges
  const earnedMilestone = MILESTONE_BADGES.filter((b) => b.check(scores));
  const unearnedMilestone = MILESTONE_BADGES.filter((b) => !b.check(scores));

  // Behavioural + referral (only when viewing own profile)
  const earnedBehavioural = badgeContext
    ? BEHAVIOURAL_BADGES.filter((b) => b.check(badgeContext))
    : [];
  const unearnedBehavioural = badgeContext
    ? BEHAVIOURAL_BADGES.filter((b) => !b.check(badgeContext))
    : [];

  const earnedReferral = badgeContext
    ? REFERRAL_BADGES.filter((b) => b.check(badgeContext))
    : [];
  const unearnedReferral = badgeContext
    ? REFERRAL_BADGES.filter((b) => !b.check(badgeContext))
    : [];

  const hasAny =
    earnedDim.length + unearnedDim.length +
    earnedMilestone.length + unearnedMilestone.length +
    (badgeContext ? BEHAVIOURAL_BADGES.length + REFERRAL_BADGES.length : 0) > 0;

  if (!hasAny) return null;

  const Section = ({ label, children }) => (
    <div style={{ marginBottom: 16 }}>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
        {label}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {children}
      </div>
    </div>
  );

  return (
    <div>
      {/* Dimension badges */}
      {(earnedDim.length > 0 || unearnedDim.length > 0) && (
        <Section label="Dimension Badges">
          {earnedDim.map((b) => (
            <Badge
              key={b.id}
              svg={b.svg}
              label={b.label}
              color={b.tier.color}
              desc={`${b.label} — ${b.tier.label} tier (score ${b.score}). Reach 50 for Bronze, 65 Silver, 80 Gold, 95 Platinum.`}
              earned
              tierLabel={b.tier.label}
            />
          ))}
          {unearnedDim.map((b) => (
            <Badge
              key={b.id}
              svg={b.svg}
              label={b.label}
              color="#6b7ab8"
              desc={`Score 50+ in this dimension to earn Bronze. Tiers: 50 Bronze · 65 Silver · 80 Gold · 95 Platinum.`}
              earned={false}
            />
          ))}
        </Section>
      )}

      {/* Overall milestone badges */}
      {(earnedMilestone.length > 0 || unearnedMilestone.length > 0) && (
        <Section label="Milestones">
          {earnedMilestone.map((b) => (
            <Badge key={b.id} svg={b.svg} label={b.label} color={b.color} desc={b.desc} earned />
          ))}
          {unearnedMilestone.map((b) => (
            <Badge key={b.id} svg={b.svg} label={b.label} color={b.color} desc={b.desc} earned={false} />
          ))}
        </Section>
      )}

      {/* Behavioural badges (own profile only) */}
      {badgeContext && (earnedBehavioural.length > 0 || unearnedBehavioural.length > 0) && (
        <Section label="Achievements">
          {earnedBehavioural.map((b) => (
            <Badge key={b.id} svg={b.svg} label={b.label} color={b.color} desc={b.desc} earned />
          ))}
          {unearnedBehavioural.map((b) => (
            <Badge key={b.id} svg={b.svg} label={b.label} color={b.color} desc={b.desc} earned={false} />
          ))}
        </Section>
      )}

      {/* Referral badges (own profile only) */}
      {badgeContext && (earnedReferral.length > 0 || unearnedReferral.length > 0) && (
        <Section label="Community">
          {earnedReferral.map((b) => (
            <Badge key={b.id} svg={b.svg} label={b.label} color={b.color} desc={b.desc} earned />
          ))}
          {unearnedReferral.map((b) => (
            <Badge key={b.id} svg={b.svg} label={b.label} color={b.color} desc={b.desc} earned={false} />
          ))}
        </Section>
      )}
    </div>
  );
}
